import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Box,
  Typography
} from '@mui/material';
import { FetchWithAuth, Category, Exercise } from '../../types';
import { API_URL } from '../../utils';
import { useLanguage } from '../../i18n/LanguageContext';
import { commonDialogStyles } from '../common/dialogStyles';

interface AddExerciseDialogProps {
  open: boolean;
  onClose: () => void;
  onExerciseAdded: (exercise: any) => void;
  exerciseToEdit?: Exercise | null;
  onExerciseUpdated?: (exercise: any) => void;
  fetchWithAuth: FetchWithAuth;
  onServiceUnavailable?: () => void;
}

export const AddExerciseDialog: React.FC<AddExerciseDialogProps> = ({
  open,
  onClose,
  onExerciseAdded,
  exerciseToEdit,
  onExerciseUpdated,
  fetchWithAuth,
  onServiceUnavailable
}) => {
  const { t } = useLanguage();
  const [name, setName] = useState('');
  const [categoryId, setCategoryId] = useState<string>('');
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<{ name?: string; category?: string }>({});

  const isEditMode = Boolean(exerciseToEdit);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/exercises/categories`);
      if (res.ok) {
        const data = await res.json();
        setCategories(data);
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  }, [fetchWithAuth]);

  useEffect(() => {
    if (open) {
      fetchCategories();
      if (isEditMode && exerciseToEdit) {
        setName(exerciseToEdit.name);
        setCategoryId(exerciseToEdit.category_id || '-1');
      } else {
        resetForm();
      }
    }
  }, [open, fetchCategories, isEditMode, exerciseToEdit]);

  const resetForm = () => {
    setName('');
    setCategoryId('-1');
    setError(null);
    setValidationErrors({});
  };

  const validateForm = (): boolean => {
    const errors: { name?: string; category?: string } = {};

    if (!name.trim()) {
      errors.name = t('exercise.validation.nameRequired');
    }

    if (!categoryId) {
      errors.category = t('exercise.validation.categoryRequired');
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setError(null);

    const exerciseData = {
      name: name.trim(),
      category_id: categoryId,
    };

    const url = isEditMode
      ? `${API_URL}/exercises/${exerciseToEdit?.id}`
      : `${API_URL}/exercises`;

    const method = isEditMode ? 'PATCH' : 'POST';

    try {
      const res = await fetchWithAuth(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(exerciseData),
      });

      if (res.ok) {
        const resultantExercise = await res.json();
        if (isEditMode) {
          onExerciseUpdated?.(resultantExercise);
        } else {
          onExerciseAdded(resultantExercise);
        }
        onClose();
      } else if (res.status === 409) {
        setError(t('exercise.exists'));
      } else {
        setError(isEditMode ? t('exercise.updateError') : t('exercise.addError'));
      }
    } catch (err: any) {
      console.error(`Error ${isEditMode ? 'updating' : 'adding'} exercise:`, err);
      if (err.isServiceUnavailable && onServiceUnavailable) {
        onServiceUnavailable();
      } else {
        setError(isEditMode ? t('exercise.updateError') : t('exercise.addError'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="sm" 
      fullWidth 
      sx={commonDialogStyles}
    >
      <DialogTitle>{isEditMode ? t('exercise.editTitle') : t('exercise.addNew')}</DialogTitle>
      <DialogContent sx={{ pt: '20px !important' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            autoFocus
            label={t('exercise.name')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            error={!!validationErrors.name}
            helperText={validationErrors.name}
            disabled={isLoading}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !isLoading) {
                handleSubmit();
              }
            }}
          />

          <FormControl fullWidth error={!!validationErrors.category}>
            <InputLabel>{t('exercise.category')}</InputLabel>
            <Select
              value={categoryId}
              label={t('exercise.category')}
              onChange={(e) => setCategoryId(e.target.value)}
              disabled={isLoading}
            >
              <MenuItem key={-1} value="-1">
                <em>{t('exercise.uncategorized')}</em>
              </MenuItem>
              {categories.map((category) => (
                <MenuItem key={category.id} value={category.id}>
                  {category.name}
                </MenuItem>
              ))}
            </Select>
            {validationErrors.category && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                {validationErrors.category}
              </Typography>
            )}
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          {t('exercise.cancel')}
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={isLoading || !name.trim() || !categoryId}
        >
          {isLoading
            ? (isEditMode ? t('exercise.saving') : t('exercise.adding'))
            : (isEditMode ? t('exercise.save') : t('exercise.addButton'))}
        </Button>
      </DialogActions>
    </Dialog>
  );
}; 